import React, { useCallback, useMemo, useState } from "react";
import { RuleGroupType, RuleSource } from "@common/types/transactionRule";
import { useScope } from "@state/scopeContext.lib";
import { NewTransactionRuleDefinition } from "@components/transactionRules/TransactionRuleCreation/TransactionRuleCreationDrawer";
import { ModalComponentProps } from "@helpers/modal";
import { useCreateRule } from "@api/rules.api";
import {
    RuleForm,
    TransactionRuleSubmitData,
} from "./TransactionRuleForm/RuleForm";
import { TransferRuleForm } from "./TransferRuleForm/TransferRuleForm";

interface Props extends ModalComponentProps {
    newRuleDefinition?: NewTransactionRuleDefinition;
    source: RuleSource;
    groupType: RuleGroupType;
    onGroupTypeChange?: (groupType: RuleGroupType) => void;
}

export const AddRuleModal: React.FC<Props> = ({
    close,
    newRuleDefinition,
    source,
    groupType,
    onGroupTypeChange: onGroupTypeChangeExternal,
}) => {
    const { workspaceId } = useScope();
    const createRule = useCreateRule(close);
    const [activeGroupType, setActiveGroupType] = useState(groupType);

    const onCreate = useCallback(
        async (data: TransactionRuleSubmitData) => {
            if (!workspaceId) {
                return;
            }

            await createRule.mutateAsync({
                workspaceId,
                source,
                ...data,
            });
        },
        [workspaceId, createRule, source],
    );

    const handleTypeChange = useCallback(
        (newType: RuleGroupType) => {
            setActiveGroupType(newType);
            onGroupTypeChangeExternal?.(newType);
        },
        [onGroupTypeChangeExternal],
    );

    const effectiveDefinition = useMemo(
        () =>
            activeGroupType === groupType
                ? newRuleDefinition
                : {
                      conditions: [],
                      ...(newRuleDefinition?.requireExplicitConditions
                          ? {
                                requireExplicitConditions: true,
                                scopeEntityId: newRuleDefinition.scopeEntityId,
                                applyToExistingTransactions:
                                    newRuleDefinition.applyToExistingTransactions,
                            }
                          : {}),
                  },
        [activeGroupType, groupType, newRuleDefinition],
    );

    if (activeGroupType === RuleGroupType.TRANSFER) {
        return (
            <TransferRuleForm
                onSubmit={onCreate}
                isSubmitting={createRule.isPending}
                submitText="Create rule"
                newRuleDefinition={effectiveDefinition}
                onTypeChange={handleTypeChange}
            />
        );
    }

    return (
        <RuleForm
            onSubmit={onCreate}
            isSubmitting={createRule.isPending}
            submitText="Create rule"
            newRuleDefinition={effectiveDefinition}
            groupType={activeGroupType}
            onTypeChange={handleTypeChange}
        />
    );
};
